import { endOfMonth, format, isValid, startOfMonth } from 'date-fns';
// import XLSX from 'xlsx';
import * as XLSX from 'xlsx';

export const secondsToMs = (seconds) => {
  seconds = Number(seconds);

  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const s = Math.floor((seconds % 3600) % 60)
    .toString()
    .padStart(2, '0');

  return `${m}:${s}`;
};

export const getMatchedMonth = (data = {}, month, startDate = new Date(), endDate = new Date()) => {
  try {
    if (isPartialMatch(endDate, month) && data[format(new Date(endDate), 'yyyy-MM-dd')]) {
      return format(new Date(endDate), 'yyyy-MM-dd');
    }
    const keys = Object.keys(data);
    let exactMatch;
    let partialMatch;

    for (const key of keys) {
      if (isExactMatch(key, month)) {
        exactMatch = key;
        break;
      }
      if (isPartialMatch(key, month)) {
        partialMatch = key;
      }
    }
    return exactMatch || partialMatch;
  } catch (error) {
    return null;
  }
};

export const isExactMatch = (date1, date2) => {
  return (
    new Date(date1).getMonth() === new Date(date2).getMonth() &&
    new Date(date1).getFullYear() === new Date(date2).getFullYear() &&
    new Date(date1).getDate() === new Date(date2).getDate()
  );
};

export const isPartialMatch = (date1, date2) => {
  return (
    new Date(date1).getMonth() === new Date(date2).getMonth() &&
    new Date(date1).getFullYear() === new Date(date2).getFullYear()
  );
};

export const getModifiedStartEndDate = (data, startDate, endDate) => {
  let updatedStartDate = startDate;
  let updatedEndDate = endDate;

  if (data && data.oldStartDate === startDate) {
    const oldEndDate = new Date(data.oldEndDate);
    const newEndDate = new Date(endDate);

    if (newEndDate < oldEndDate) {
      const firstDay = startOfMonth(newEndDate);
      updatedStartDate = format(firstDay, 'yyyy-MM-dd');
    } else {
      const firstDay = startOfMonth(oldEndDate);
      updatedStartDate = format(firstDay, 'yyyy-MM-dd');
    }
  } else if (data && data.oldEndDate === endDate) {
    const oldStartDate = new Date(data.oldStartDate);
    const newStartDate = new Date(startDate);

    if (newStartDate > oldStartDate) {
      const lastDay = endOfMonth(newStartDate);
      updatedEndDate = format(lastDay, 'yyyy-MM-dd');
    } else {
      const lastDay = endOfMonth(oldStartDate);
      updatedEndDate = format(lastDay, 'yyyy-MM-dd');
    }
  }
  return {
    startDate: updatedStartDate,
    endDate: updatedEndDate,
  };
};

export async function downloadTableAsExcel(tableElement, filename) {
  return new Promise((resolve, reject) => {
    const table = tableElement.cloneNode(true);

    table.querySelectorAll('td').forEach((cell) => {
      cell.style.textAlign = 'center';
    });

    table.querySelectorAll('[data-excel]').forEach((node) => {
      node.removeAttribute('data-excel');
    });

    const worksheet = XLSX.utils.table_to_sheet(table);
    // Iterate through the cells and set the format to "Text" for numbers
    for (let cellAddress in worksheet) {
      const cell = worksheet[cellAddress];
      if (cell.t === 'n') {
        cell.t = 's'; // Set the cell format to text
        cell.z = '0'; // Format as text (zero-length string)
      }
    }
    // worksheet['!cols'] = [{ wch: 50 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Generate Excel binary data
    const excelData = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' });

    // Convert binary data to Blob
    const blob = new Blob([s2ab(excelData)], { type: 'application/octet-stream' });

    // Create a download link
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = filename || 'table.xlsx';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    resolve();
  });
}

export function s2ab(s) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i !== s.length; ++i) {
    view[i] = s.charCodeAt(i) & 0xff;
  }
  return buf;
}

export const groupDataByEnterpriseId = (data) => {
  const groupedData = {};
  data.forEach((entry) => {
    const endDate = new Date(entry.active_date);
    // const monthYear = `${endDate.getFullYear()}-${endDate.getMonth()}-${endDate.getDate()}`;
    const monthYear = format(endDate, 'MMMM');
    if (!groupedData[entry.enterpriseId]) {
      groupedData[entry.enterpriseId] = {
        enterpriseName: entry.enterpriseName,
        activeUsers: {},
        totalMinsPlayed: {},
        totalSteps: {},
        totalUsersSyncingSteps: {},
        totalAppShares: {},
        totalChallengesJoined: {},
        totalChallengesParticipated: {},
        totalPcCompleted: {},
        totalUsersTakenPc: {},
        totalUsersRatedContent: {},
        totalRatingsGiven: {},
        positiveRatingsGiven: {},
        totalWaterIntake: {},
        totalUsersTakingWaterIntake: {},
        totalNumberOfFavorite: {},
        totalUsersFavoriteTheContent: {},
      };
    }
    groupedData[entry.enterpriseId].activeUsers[monthYear] = entry.activeUsers;
    groupedData[entry.enterpriseId].totalMinsPlayed[monthYear] = entry.total_mins;
    groupedData[entry.enterpriseId].totalSteps[monthYear] = entry.total_steps;
    groupedData[entry.enterpriseId].totalUsersSyncingSteps[monthYear] =
      entry.total_users_syncing_steps;
    groupedData[entry.enterpriseId].totalAppShares[monthYear] = entry.total_number_of_app_shares;
    groupedData[entry.enterpriseId].totalChallengesJoined[monthYear] =
      entry.total_challenges_joined;
    groupedData[entry.enterpriseId].totalChallengesParticipated[monthYear] =
      entry.total_challenges_participated;
    groupedData[entry.enterpriseId].totalPcCompleted[monthYear] =
      entry.personal_coaching_bookings_completed;
    groupedData[entry.enterpriseId].totalUsersTakenPc[monthYear] =
      entry.users_taken_personal_coaching;
    groupedData[entry.enterpriseId].totalUsersRatedContent[monthYear] =
      entry.total_users_rated_content;
    groupedData[entry.enterpriseId].totalRatingsGiven[monthYear] = entry.total_ratings_given;
    groupedData[entry.enterpriseId].positiveRatingsGiven[monthYear] = entry.positive_ratings_given;
    groupedData[entry.enterpriseId].totalWaterIntake[monthYear] = entry.todays_water_intake;
    groupedData[entry.enterpriseId].totalUsersTakingWaterIntake[monthYear] =
      entry.total_users_taking_waterintake;
    groupedData[entry.enterpriseId].totalNumberOfFavorite[monthYear] =
      entry.total_number_of_favorite;
    groupedData[entry.enterpriseId].totalUsersFavoriteTheContent[monthYear] =
      entry.total_users_favorite_the_content;
  });
  return groupedData;
};

const headerRangeMap = {};

export const flattenData = (data) => {
  const result = [];
  let headerPushed = false;
  let subHeaderPushed = false;

  for (const value of Object.values(data)) {
    let headers = [];
    const subHeader = [];
    const row = [];
    for (const [key, data] of Object.entries(value)) {
      if (typeof data === 'string') {
        subHeader.push('');
        row.push(data);
        headers.push(key);
        headerRangeMap[key] = 1;
      } else {
        const entries = Object.entries(data);
        for (const [key2, data2] of entries) {
          subHeader.push(key2);
          row.push(data2);
        }
        headers = headers.concat([...Array(entries.length).keys()].map((i) => key));
        headerRangeMap[key] = Math.max(headerRangeMap[key] || 0, entries.length);
      }
    }
    if (!headerPushed) {
      result.push(headers);
      headerPushed = true;
      console.log('headers', headers);
    }
    if (!subHeaderPushed) {
      result.push(subHeader);
      subHeaderPushed = true;
    }
    result.push(row);
  }
  console.table(result);
  return result;
};

export const downloadOverallReportSheet = (flatData, filename) => {
  const wb = XLSX.utils.book_new();

  // Create a worksheet
  const ws = XLSX.utils.aoa_to_sheet(flatData);

  const mergeRange = [
    {
      s: { r: 0, c: 0 },
      e: { r: 0, c: 0 },
    },
  ];

  const headers = flatData[0];
  console.log('headers', headers);
  console.log('headerRangeMap', headerRangeMap);

  let prevRange = 1;
  for (let i = 0; i < headers.length; i++) {
    const range = headerRangeMap[headers[prevRange]];
    prevRange = range;

    mergeRange.push({
      s: { r: 0, c: 1 + range * i },
      e: { r: 0, c: 1 + range * i + range - 1 },
    });
  }
  ws['!merges'] = mergeRange;

  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  // Save the workbook as an Excel file
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const getLastThreeMonthsData = (data, field, divideBy) => {
  let sum = 0;
  const lastThreeMonths = data.slice(data.length - 3, data.length);
  lastThreeMonths.forEach((value) => {
    sum += Number(value);
  });
  if (divideBy) {
    return (sum / divideBy).toLocaleString('en-US');
  }
  return sum.toLocaleString('en-US');
};

export function getQuartersTillToday() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const quarters = [];

  for (let quarter = 1; quarter <= 4; quarter++) {
    const quarterStartMonth = (quarter - 1) * 3 + 1;
    const quarterStartDate = new Date(`${currentYear}-${quarterStartMonth}-01`);

    let quarterEndMonth = quarter * 3;
    let quarterEndDate = new Date(`${currentYear}-${quarterEndMonth}-01`);
    quarterEndDate.setMonth(quarterEndDate.getMonth() + 1);
    quarterEndDate.setDate(0);

    if (quarter * 3 <= currentMonth) {
      quarters.push({
        startDate: format(quarterStartDate, 'yyyy-MM-dd'),
        endDate: format(quarterEndDate, 'yyyy-MM-dd'),
        quarter: `Quarter ${quarter}`,
      });
    } else if (quarterStartDate <= today) {
      quarters.push({
        startDate: format(quarterStartDate, 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd'),
        quarter: `Quarter ${quarter}`,
      });
    }
  }

  return quarters;
}

export function firstDateOfQuarter(inputDate) {
  const inputDateTime = new Date(inputDate);
  const year = inputDateTime.getFullYear();
  const month = inputDateTime.getMonth() + 1;

  const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
  const firstDayOfQuarter = new Date(year, quarterStartMonth - 1, 1);
  const resultDate = format(firstDayOfQuarter, 'yyyy-MM-dd');
  return resultDate;
}

export function getQuarterEndDates(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const quarterEndDates = [];

  while (start <= end) {
    const quarterEndMonth = Math.floor(start.getMonth() / 3) * 3 + 2; // 0-indexed quarters
    const quarterEndDate = endOfMonth(new Date(start.getFullYear(), quarterEndMonth));

    if (quarterEndDate >= start && quarterEndDate <= end) {
      quarterEndDates.push({
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(quarterEndDate, 'yyyy-MM-dd'),
        quarter: `Quarter ${Math.floor(quarterEndDate.getMonth() / 3 + 1)}`,
        quarterNo: Math.floor(quarterEndDate.getMonth() / 3 + 1),
      });
    } else if (quarterEndDate > end) {
      quarterEndDates.push({
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
        quarter: `Quarter ${Math.floor(end.getMonth() / 3 + 1)}`,
        quarterNo: Math.floor(Math.floor(end.getMonth() / 3 + 1)),
      });
    }

    start.setMonth(quarterEndMonth + 1);
    start.setDate(1);
  }

  return quarterEndDates;
}

export const getDateFromString = (date) => {
  const d = new Date(`${date}T00:00:00`);
  if (isValid(d)) {
    return d;
  }
  return new Date(date);
};

export const isCurrentMonth = (month) => {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthDate = new Date(month).getMonth();
  const monthYear = new Date(month).getFullYear();
  return currentMonth === monthDate && currentYear === monthYear;
};
